import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { sanitize } from "../../lib/sanitize";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ArrowLeft, PaperPlaneTilt, FilePdf, Copy, Link, PencilSimple, ArrowClockwise, EnvelopeSimple, Clock, UserCircle, WhatsappLogo, Warning } from "@phosphor-icons/react";
import { AttachmentGrid } from "../ui/AttachmentGrid";

const STATUS_CONFIG = {
  borrador: { label: "Borrador", color: "bg-slate-100 text-slate-700 border-slate-200" },
  pendiente: { label: "Pendiente", color: "bg-amber-100 text-amber-800 border-amber-200" },
  aprobado: { label: "Aprobado", color: "bg-green-100 text-green-800 border-green-200" },
  rechazado: { label: "Rechazado", color: "bg-red-100 text-red-800 border-red-200" },
  vencido: { label: "Vencido", color: "bg-gray-100 text-gray-600 border-gray-200" },
};

const fmtMXN = (v) => (v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtUSD = (v) => (v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mediaUrl = (path) => { if (!path) return ""; return `${API}/quotations/media/${path.replace(/^quotations\//, "")}`; };
const whatsappQR = (num) => `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`https://wa.me/${num}`)}`;

export default function QuotationDetail({ token, quotationId, onNavigate }) {
  const [q, setQ] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [qRes, cRes] = await Promise.all([
        axios.get(`${API}/quotations/${quotationId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/quotations/config`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setQ(qRes.data);
      setConfig(cRes.data || {});
    } catch { toast.error("Error al cargar cotizacion"); }
    finally { setLoading(false); }
  }, [token, quotationId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSend = async () => {
    try {
      await axios.post(`${API}/quotations/${quotationId}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Cotizacion enviada"); fetchData();
    } catch { toast.error("Error al enviar"); }
  };

  const handleResendEmail = async () => {
    setSendingEmail(true);
    try {
      await axios.post(`${API}/quotations/${quotationId}/resend-email`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Email reenviado exitosamente");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar email"); }
    finally { setSendingEmail(false); }
  };

  const handlePdf = async () => {
    try {
      const res = await axios.get(`${API}/quotations/${quotationId}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `cotizacion_${q?.quotation_number || ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al generar PDF"); }
  };

  const copyPublicLink = () => {
    if (!q?.public_token) return;
    navigator.clipboard.writeText(`${window.location.origin}/cotizacion/${q.public_token}`);
    toast.success("Link publico copiado");
  };

  if (loading) return <div className="flex justify-center py-20"><ArrowClockwise size={32} className="animate-spin text-blue-500" /></div>;
  if (!q) return <div className="text-center py-20 text-slate-400">Cotizacion no encontrada</div>;

  const sc = STATUS_CONFIG[q.status] || STATUS_CONFIG.borrador;
  const hasDiscounts = q.global_discount_percent > 0 || (q.items || []).some(i => i.discount_percent > 0);
  const attachments = q.attachments || [];
  const managers = config.managers || [];
  const urgencyMessages = config.urgency_messages || [];
  const bannerImages = config.banner_images || [];
  const termsTemplates = config.terms_templates || [];
  const selectedTermsIds = q.terms_template_ids || (q.terms_template_id ? [q.terms_template_id] : []);
  const selectedTerms = termsTemplates.filter(t => selectedTermsIds.includes(t.id));
  const subtotalMxn = q.subtotal_mxn || q.total_mxn || 0;
  const subtotalUsd = q.subtotal_usd || q.total_usd || 0;
  const hasGlobalDiscount = (q.global_discount_percent || 0) > 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="quotation-detail" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate("list")} className="p-2 rounded-lg hover:bg-slate-100"><ArrowLeft size={20} /></button>
          <div>
            <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "Montserrat, sans-serif" }}>{q.quotation_number}</h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${sc.color}`}>{sc.label}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {q.status === "borrador" && <Button variant="outline" size="sm" onClick={() => onNavigate("edit", null, q.id)}><PencilSimple size={16} className="mr-1" /> Editar</Button>}
          {q.status === "borrador" && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleSend}><PaperPlaneTilt size={16} className="mr-1" /> Enviar</Button>}
          {q.status !== "borrador" && q.client_email && (
            <Button variant="outline" size="sm" onClick={handleResendEmail} disabled={sendingEmail} data-testid="resend-email-btn">
              <EnvelopeSimple size={16} className="mr-1" /> {sendingEmail ? "Enviando..." : "Reenviar Email"}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={copyPublicLink} data-testid="copy-public-link"><Link size={16} className="mr-1" /> Link Publico</Button>
          <Button variant="outline" size="sm" className="text-red-600" onClick={handlePdf} data-testid="download-pdf"><FilePdf size={16} className="mr-1" /> PDF</Button>
        </div>
      </div>

      {/* Validity + Urgency Banner */}
      <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-4 text-white shadow-lg" data-testid="validity-banner">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Clock size={28} weight="fill" />
            <div>
              <p className="font-bold text-lg" style={{ fontFamily: "Montserrat, sans-serif" }}>VIGENCIA: {q.validity_days} DIAS</p>
              {q.expires_at && <p className="text-sm text-white/80">Vence: {q.expires_at.slice(0, 10)}</p>}
            </div>
          </div>
          {urgencyMessages.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {urgencyMessages.map((msg, i) => (
                <span key={`msg-${msg.slice(0,10)}-${i}`} className="px-3 py-1 bg-white/20 backdrop-blur rounded-full text-xs font-semibold flex items-center gap-1">
                  <Warning size={12} weight="fill" /> {msg}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Client + Creator info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border p-5">
          <h3 className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Datos del Cliente</h3>
          <p className="text-xl font-bold text-blue-800" style={{ fontFamily: "Montserrat, sans-serif" }}>{q.client_company || "Sin empresa"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <div><p className="text-xs text-slate-400">Encargado</p><p className="font-medium text-sm">{q.client_name}</p></div>
            <div><p className="text-xs text-slate-400">Email</p><p className="font-medium text-sm">{q.client_email || "-"}</p></div>
            <div><p className="text-xs text-slate-400">Telefono</p><p className="font-medium text-sm">{q.client_phone || "-"}</p></div>
          </div>
        </div>

        {/* Creator info */}
        <div className="bg-white rounded-xl border p-5 flex flex-col items-center text-center" data-testid="creator-info">
          <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider self-start" style={{ fontFamily: "Montserrat, sans-serif" }}>Creado por</p>
          {q.created_by_photo ? (
            <img src={mediaUrl(q.created_by_photo)} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-blue-200 mb-2" />
          ) : (
            <UserCircle size={64} className="text-blue-200 mb-2" weight="fill" />
          )}
          <p className="font-bold text-sm text-slate-800" style={{ fontFamily: "Montserrat, sans-serif" }}>{q.created_by_name}</p>
          {q.created_by_position && <p className="text-xs text-blue-600 font-medium">{q.created_by_position}</p>}
          {q.created_by_email && <p className="text-xs text-slate-500 mt-1">{q.created_by_email}</p>}
          {q.created_by_phone && <p className="text-xs text-slate-500">{q.created_by_phone}</p>}
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Detalle de la Cotizacion</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-blue-600 text-white" style={{ fontFamily: "Montserrat, sans-serif" }}>
              <th className="px-3 py-2.5 text-left rounded-tl-lg">#</th>
              <th className="px-3 py-2.5 text-left">Producto</th>
              <th className="px-3 py-2.5 text-center">Cant.</th>
              <th className="px-3 py-2.5 text-right">P.U. USD</th>
              <th className="px-3 py-2.5 text-right">P.U. MXN</th>
              {hasDiscounts && <th className="px-3 py-2.5 text-center">Desc.%</th>}
              <th className="px-3 py-2.5 text-right">Total MXN</th>
              <th className="px-3 py-2.5 text-right rounded-tr-lg">Total USD</th>
            </tr></thead>
            <tbody>
              {(q.items || []).map((item, i) => (
                <tr key={item.id || `item-${i}`} className={`${i % 2 ? "bg-white" : "bg-blue-50/30"} border-b border-slate-100`}>
                  <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{item.product_name || item.description}</span>
                    {item.description && item.product_name && <span className="block text-xs text-slate-400">{item.description}</span>}
                  </td>
                  <td className="px-3 py-2 text-center">{item.quantity}</td>
                  <td className="px-3 py-2 text-right">${fmtUSD(item.unit_price_usd)}</td>
                  <td className="px-3 py-2 text-right">${fmtMXN(item.unit_price_mxn)}</td>
                  {hasDiscounts && <td className="px-3 py-2 text-center text-orange-600">{item.discount_percent > 0 ? `${item.discount_percent}%` : "-"}</td>}
                  <td className="px-3 py-2 text-right font-semibold text-blue-700">${fmtMXN(item.total_mxn)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-green-700">${fmtUSD(item.total_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals - new pricing layout */}
        <div className="mt-5 pt-4 border-t space-y-2 text-sm">
          <div className="flex justify-end items-center gap-4">
            <span className="text-slate-500 w-52 text-right" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600 }}>Subtotal:</span>
            <span className="w-36 text-right font-bold text-lg text-slate-700">${fmtMXN(subtotalMxn)} MXN</span>
            <span className="w-28 text-right text-slate-400">${fmtUSD(subtotalUsd)} USD</span>
          </div>
          {hasGlobalDiscount && (
            <div className="flex justify-end items-center gap-4 text-orange-600">
              <span className="w-52 text-right font-medium italic">Oferta valida por tiempo limitado ({q.global_discount_percent}%):</span>
              <span className="w-36 text-right font-bold">-${fmtMXN(q.global_discount_mxn)} MXN</span>
              <span className="w-28 text-right">-${fmtUSD(q.global_discount_usd)} USD</span>
            </div>
          )}
          {(q.taxes_detail || []).map((t, i) => (
            <div key={t.name || `tax-${i}`} className="flex justify-end items-center gap-4 text-slate-600">
              <span className="w-52 text-right">{t.name} ({t.rate}%):</span>
              <span className="w-36 text-right">${fmtMXN(t.amount_mxn)} MXN</span>
              <span className="w-28 text-right">${fmtUSD(t.amount_usd)} USD</span>
            </div>
          ))}
          {/* PRECIO ESPECIAL */}
          <div className="mt-2 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-5 text-white" data-testid="special-price">
            <p className="text-xs uppercase tracking-widest text-blue-200 mb-1" style={{ fontFamily: "Montserrat, sans-serif" }}>Precio Especial</p>
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <p className="text-4xl font-black" style={{ fontFamily: "Montserrat, sans-serif" }}>${fmtMXN(q.total_mxn)} <span className="text-lg font-semibold text-blue-200">MXN</span></p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-300">${fmtUSD(q.total_usd)} <span className="text-sm text-green-200">USD</span></p>
                <p className="text-xs text-blue-200 mt-0.5">T.C.: 1 USD = ${q.exchange_rate} MXN</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms and metadata */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Terminos</h3>
          <p className="text-sm"><b>Validez:</b> {q.validity_days} dias{q.expires_at ? ` (vence: ${q.expires_at.slice(0, 10)})` : ""}</p>
          {q.payment_terms && q.payment_terms !== "<p><br></p>" && (
            <div className="mt-3">
              <p className="text-sm font-semibold text-slate-700 mb-1">Forma de pago:</p>
              <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-headings:my-1" dangerouslySetInnerHTML={sanitize(q.payment_terms)} />
            </div>
          )}
          {q.notes && q.notes !== "<p><br></p>" && (
            <div className="mt-3">
              <p className="text-sm font-semibold text-slate-700 mb-1">Notas:</p>
              <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-headings:my-1" dangerouslySetInnerHTML={sanitize(q.notes)} />
            </div>
          )}
        </div>

        {/* Terms & Conditions */}
        {selectedTerms.length > 0 && (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Terminos y Condiciones</h3>
            <div className="space-y-3">
              {selectedTerms.map(tmpl => (
                <div key={tmpl.id} className="rounded-lg border overflow-hidden">
                  <div className="px-3 py-2 text-xs font-semibold text-white" style={{ backgroundColor: tmpl.font_color || "#1E293B" }}>
                    {tmpl.name}
                  </div>
                  <div className="px-4 py-3 bg-slate-50/80 prose prose-sm max-w-none" style={{
                    color: tmpl.font_color || "#1E293B",
                    fontFamily: tmpl.font_family || "Helvetica, sans-serif",
                    fontSize: `${tmpl.font_size || 12}px`,
                    lineHeight: 1.5,
                  }} dangerouslySetInnerHTML={sanitize(tmpl.content || "")} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Informacion</h3>
          <p className="text-sm"><b>Creado por:</b> {q.created_by_name}</p>
          <p className="text-sm"><b>Creado:</b> {(q.created_at || "").slice(0, 16).replace("T", " ")}</p>
          {q.sent_at && <p className="text-sm"><b>Enviado:</b> {q.sent_at.slice(0, 16).replace("T", " ")}</p>}
          {q.responded_at && <p className="text-sm"><b>Respondido:</b> {q.responded_at.slice(0, 16).replace("T", " ")}</p>}
          {q.client_comment && <div className="mt-3 p-3 bg-blue-50 rounded-lg"><p className="text-xs text-slate-500 mb-1">Comentario del cliente:</p><p className="text-sm">{q.client_comment}</p></div>}
        </div>
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Archivos Adjuntos ({attachments.length})</h3>
          <AttachmentGrid attachments={attachments} apiBase={API} />
        </div>
      )}

      {/* Managers */}
      {managers.length > 0 && (
        <div className="bg-white rounded-xl border p-5" data-testid="managers-section">
          <h3 className="text-xs font-semibold text-slate-400 mb-4 uppercase tracking-wider" style={{ fontFamily: "Montserrat, sans-serif" }}>Contactos de Venta</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {managers.map((mgr, i) => (
              <div key={mgr.id || mgr.name || `mgr-${i}`} className="flex gap-4 items-center bg-slate-50 rounded-xl p-4 border">
                <div className="shrink-0">
                  {mgr.photo_path ? (
                    <img src={mediaUrl(mgr.photo_path)} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-blue-200" />
                  ) : (
                    <UserCircle size={64} className="text-slate-300" weight="fill" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-slate-800" style={{ fontFamily: "Montserrat, sans-serif" }}>{mgr.name}</p>
                  {mgr.position && <p className="text-xs text-blue-600">{mgr.position}</p>}
                  {mgr.email && <p className="text-xs text-slate-500">{mgr.email}</p>}
                  {mgr.phone && <p className="text-xs text-slate-500">{mgr.phone}</p>}
                </div>
                {mgr.whatsapp && (
                  <div className="shrink-0 flex flex-col items-center">
                    <img src={whatsappQR(mgr.whatsapp)} alt="QR WhatsApp" className="w-20 h-20 rounded-lg border" />
                    <a href={`https://wa.me/${mgr.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-green-600 mt-1 hover:underline">
                      <WhatsappLogo size={12} weight="fill" /> WhatsApp
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Banners */}
      {bannerImages.length > 0 && (
        <div className="space-y-3" data-testid="banner-section">
          {bannerImages.map((b, i) => (
            <div key={b.id || b.path || `banner-${i}`} className="w-full rounded-xl overflow-hidden border shadow-sm">
              <img src={mediaUrl(b.path || b)} alt={b.filename || `Banner ${i + 1}`} className="w-full h-auto max-h-48 object-cover" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
